import isEmpty from 'lodash/isEmpty';
import { useEffect, useState } from 'react';
import styles from './DirectorDetails.module.scss';
import DirectorInputField from './Components/DirectorInputField';
import { numberOnly } from '../../../../../utils/utils';
import clsx from 'clsx';
import { Input } from '../../../../../common/_custom';
import { useDispatch } from 'react-redux';
import { sendCibilConsentLink } from '../../../../../store/company/action';
import { useAppSelector } from '../../../../../app/hooks';
import { toast } from 'react-toastify';
import { GREEN_BG_WHITE_TICK_CIRCLE, orangeCross, uploadEquifax, verifyOrange } from '../../../../../assets/hostedassets';
import { lightBulbBlack, PURPLE_CIRCULAR_CROSS, VERIFY_GREY_CIRCLE, VERIFY_PURPLE_CIRCLE } from '../../../../../assets/hostedassets';
import { Mixpanel } from '../../../../../utils/mixpanel';
import { COMPANY_CB_ADD_DIRECTOR, COMPANY_CB_VERIFY_DIRECTOR } from '../../../../../utils/constants/mixpanelEvents/companyEvents';

import { useParams } from 'react-router-dom';
import LoadingSpinner from '../../../../../common/_custom/LoadingSpinner/LoadingSpinner';
import { USER_TYPE } from '../../../../../utils/constants/user';
import CrossFilledIcon from '../../../../../assets/Svg/crossFilledIcon';
import FetchFillIcon from '../../../../../assets/Svg/FetchFilIcon';
import { validatePAN } from '../../../../../utils/validatePAN';

const renderAsset = (asset: string | React.ReactNode) => {
    if (typeof asset === 'string') {
        return <img src={asset} alt="" className={styles.VerifyIcon} />;
    }
    return asset;
};

interface ConsentStatus {
    [key: string]: {
        bureauName: string;
        lastFetchedAt: Date;
        isConsentGiven: boolean;
    };
}

export interface IDirectorDetails {
    directorName: string;
    phoneNo: string;
    consentStatus?: ConsentStatus;
    lastInviteSentAt?: string;
    isNewDir?: boolean;
    isLoading?: boolean;
    isError: boolean;
    errorMessage: string;
    updatedAt?: number;
    cibilConsentStatus?: ConsentStatus;
    pan?: string;
}

interface CreditBureauConfig {
    displayName: string;
    name: string;
    styles?: {
        logoStyles?: {
            width?: string;
        };
    };
    logoUrl?: string;
    isDirectorInputToBeTaken: boolean;
    heading?: string;
    subHeading?: string;
    dataToDisplay: string;
    placeholder?: string;
    isActive: boolean;
    isDefault: boolean;
}

interface DirectorDetailsProps {
    isInvestorSide?: boolean;
    setConsent?: (consent: boolean) => void;
    creditBureauConfig: CreditBureauConfig;
    directorDetails: IDirectorDetails[];
    setDirectorDetails: React.Dispatch<React.SetStateAction<Record<string, IDirectorDetails[]>>>;
}
function DirectorDetails({ isInvestorSide, setConsent, creditBureauConfig, directorDetails, setDirectorDetails }: DirectorDetailsProps) {
    const dispatch = useDispatch();
    const managementList = useAppSelector((state: any) => state.company?.uploadJourneyData?.cibilSoftPullData?.managementList);

    const { id } = useParams();
    const userData = useAppSelector((state) => state.user.userData);
    const [disableSendLink, setDisableSendLink] = useState<boolean>(true);

    const validateInput = (value: string, name: string) => {
        if (name === 'phoneNo') return numberOnly(value)?.length == 10;
        if (name === 'directorName') return value?.trim() !== '';
        if (name === 'pan') return validatePAN(value);
        return false;
    };

    const dataToDisplay = creditBureauConfig?.dataToDisplay;

    const bureauName = creditBureauConfig?.name;

    const handleChange = (e: string, index: number, name: string) => {
        let value = e;
        if (name === 'phoneNo') {
            value = numberOnly(value);
            if (value.length > 10) {
                return;
            }

            if (value.length === 10 && directorDetails[index].directorName.trim() !== '') {
                setDisableSendLink(false);
            } else {
                setDisableSendLink(true);
            }

            if (value.length < 10 && value.length > 0) {
                setDirectorDetails((prev) => {
                    return {
                        ...prev,
                        [bureauName]: prev[bureauName].map((d, i) =>
                            i === index ? { ...d, isError: true, errorMessage: 'Please enter a valid phone number' } : d,
                        ),
                    };
                });
            } else if (value.length == 10 || value.length == 0) {
                setDirectorDetails((prev) => {
                    const updatedBureau = [...prev[bureauName]];
                    updatedBureau[index] = {
                        ...updatedBureau[index],
                        isError: false,
                        errorMessage: '',
                    };

                    return {
                        ...prev,
                        [bureauName]: updatedBureau,
                    };
                });
            }
        } else if (name === 'directorName') {
            if (value.trim() !== '' && dataToDisplay && validateInput(directorDetails[index]?.[dataToDisplay], dataToDisplay)) {
                setDisableSendLink(false);
            } else {
                setDisableSendLink(true);
            }
        } else if (name === 'pan') {
            const isPanValid = validatePAN(value);
            if (isPanValid && directorDetails[index].directorName.trim() !== '') {
                setDisableSendLink(false);
            } else {
                setDisableSendLink(true);
            }
            if (!isPanValid && value.length > 0) {
                setDirectorDetails((prev) => {
                    const updatedBureau = [...prev[bureauName]];
                    updatedBureau[index] = {
                        ...updatedBureau[index],
                        isError: true,
                        errorMessage: 'Please enter a valid PAN number',
                    };

                    return {
                        ...prev,
                        [bureauName]: updatedBureau,
                    };
                });
            } else if (isPanValid || value.length == 0) {
                setDirectorDetails((prev) => {
                    const updatedBureau = [...prev[bureauName]];
                    updatedBureau[index] = {
                        ...updatedBureau[index],
                        isError: false,
                        errorMessage: '',
                    };

                    return {
                        ...prev,
                        [bureauName]: updatedBureau,
                    };
                });
            }
        }
        setDirectorDetails((prev) => {
            const nextForBureau = prev[bureauName].map((d, i) => (i === index ? { ...d, [name]: value } : d));
            return { ...prev, [bureauName]: nextForBureau };
        });
    };
    const removeDirector = (index: number) => {
        setDirectorDetails((prev) => ({
            ...prev,
            [bureauName]: prev[bureauName].filter((_, i) => i !== index),
        }));
    };
    const addDirector = () => {
        Mixpanel.track(COMPANY_CB_ADD_DIRECTOR);
        const newDirector: IDirectorDetails = {
            directorName: '',
            phoneNo: '',
            consentStatus: {},
            lastInviteSentAt: undefined,
            isNewDir: true,
            isError: false,
            errorMessage: '',
            isLoading: false,
            pan: '',
        };
        setDirectorDetails((prev) => ({
            ...prev,
            [bureauName]: [...(prev[bureauName] ?? []), newDirector],
        }));
    };

    const getCibilConsentStatus = () => {
        return {
            bureauName: creditBureauConfig.name,
            lastFetchedAt: new Date(),
            isConsentGiven: true,
        };
    };

    const newConsentStatus: ConsentStatus = { [creditBureauConfig.name]: getCibilConsentStatus() };

    const syncDirectorAcrossBureaus = (
        prevState: Record<string, IDirectorDetails[]>,
        currentBureau: string,
        updatedDirector: IDirectorDetails,
        index: number,
        isNewDirector: boolean = false,
    ): Record<string, IDirectorDetails[]> => {
        const result = {
            ...prevState,
            [currentBureau]: prevState[currentBureau].map((director, idx) => (idx === index ? updatedDirector : director)),
        };

        if (!isNewDirector) return result;

        // Add new director to other bureaus without consent status
        for (const bureau in result) {
            if (bureau !== currentBureau) {
                result[bureau].push({
                    ...updatedDirector,
                    consentStatus: undefined,
                });
            }
        }

        return result;
    };

    const sendLink = (
        name: string,
        setDisableSendLink: (value: boolean) => void,
        index: number,
        source: string,
        flowType?: string,
        phoneNo?: string,
        pan?: string,
    ) => {
        Mixpanel.track(COMPANY_CB_VERIFY_DIRECTOR);
        setDisableSendLink(false);
        setDirectorDetails((prev) => ({
            ...prev,
            [bureauName]: prev[bureauName].map((director, idx) => (idx === index ? { ...director, isLoading: true } : director)),
        }));

        dispatch(
            sendCibilConsentLink({
                assesseeOrgId: userData.assesseeOrgId ? userData.assesseeOrgId : id || '',
                directorName: name,
                phoneNumber: phoneNo,
                email: userData.primaryEmail,
                source: source,
                flowType,
                pan: pan,
                onSuccess: () => {
                    setDirectorDetails((prev) => {
                        const isNewDirector = prev[bureauName][index].isNewDir;
                        const updatedDirector = {
                            ...prev[bureauName][index],
                            isLoading: false,
                            consentStatus: { ...prev[bureauName][index]?.consentStatus, ...newConsentStatus },
                            isNewDir: false,
                            updatedAt: new Date().getTime(),
                            isError: false,
                            errorMessage: '',
                        };
                        return syncDirectorAcrossBureaus(prev, bureauName, updatedDirector, index, isNewDirector);
                    });
                    if (setConsent) {
                        setConsent(true);
                    }
                    setDisableSendLink(true);
                    toast.success('Verification Successful');
                },
                onError: (message: string) => {
                    if (message === 'Error in getting cibil report from Decentro' || message.includes('Inquiry Purpose is invalid')) {
                        toast.error("We're currently facing issues and are resolving them. Please try again later.");
                    } else {
                        if (message && message.toUpperCase().includes('FAILURE')) message = 'Consumer Not found in Bureau';
                        setDirectorDetails((prev) => ({
                            ...prev,
                            [bureauName]: prev[bureauName].map((director, idx) =>
                                idx === index ? { ...director, isLoading: false, isError: true, errorMessage: message } : director,
                            ),
                        }));

                        setDisableSendLink(true);
                    }
                },
            }),
        );
    };

    const getVerifyIconLink = (director: IDirectorDetails) => {
        if (!director.isNewDir) return VERIFY_GREY_CIRCLE;
        if (dataToDisplay && validateInput(director?.[dataToDisplay], dataToDisplay))
            return <FetchFillIcon colour="var(--primary-text-colour)" height="15" width="15" className={styles.VerifyIcon} />;
        return VERIFY_GREY_CIRCLE;
    };

    const isConsentGiven = (director: IDirectorDetails) => {
        return director?.consentStatus?.[creditBureauConfig?.name]?.isConsentGiven;
    };

    return (
        <div>
            {isInvestorSide ? (
                <div className={styles.Heading}>
                    {renderAsset(creditBureauConfig?.logoUrl)}
                    <p>{creditBureauConfig?.heading}</p>
                </div>
            ) : (
                <div>
                    <h3 className={styles.MainHeading}>{creditBureauConfig?.heading} </h3>
                    <div className={styles.MainBanner}>
                        {renderAsset(lightBulbBlack)}
                        <p className="subHeading">{creditBureauConfig?.subHeading}</p>
                    </div>
                </div>
            )}
            <div className={styles.DirectorDetailsContainer}>
                {directorDetails?.map((director, index) => {
                    if (!director.isNewDir) {
                        return (
                            <div key={index}>
                                <DirectorInputField
                                    director={director}
                                    index={index}
                                    handleChange={handleChange}
                                    sendLink={sendLink}
                                    errorMessage={director.errorMessage}
                                    isError={director.isError}
                                    isLoading={director.isLoading}
                                    isInvestorSide={isInvestorSide}
                                    placeHolder={creditBureauConfig?.placeholder}
                                    creditBureauConfig={creditBureauConfig}
                                    validateInput={validateInput}
                                />
                            </div>
                        );
                    } else {
                        return (
                            <div key={index} className={styles.dflex}>
                                <div style={{ width: '40%' }} className={styles.FormContainer}>
                                    <div style={{ margin: '0 0 1rem 0' }} className={styles.Field}>
                                        <Input
                                            value={director.directorName}
                                            style={{ margin: 0 }}
                                            onChange={(e) => {
                                                handleChange(e, index, 'directorName');
                                            }}
                                            placeholder="Director Name"
                                        />
                                    </div>
                                </div>
                                <div style={{ width: '60%' }} className={styles.FormContainer}>
                                    <div style={{ margin: '0 0 1rem 0' }} className={styles.Field}>
                                        <Input
                                            name={dataToDisplay}
                                            value={director?.[dataToDisplay]}
                                            style={{ margin: 0 }}
                                            onChange={(e) => {
                                                handleChange(e, index, dataToDisplay);
                                            }}
                                            disabled={false}
                                            placeholder={creditBureauConfig?.placeholder}
                                            errorMessage={director.errorMessage}
                                            isValid={!director.isError}
                                        />
                                        {director?.[dataToDisplay] &&
                                            (isConsentGiven(director) ? (
                                                <div className={styles.BannerTextVerified}>
                                                    {renderAsset(GREEN_BG_WHITE_TICK_CIRCLE)}
                                                    <span>Fetched</span>
                                                </div>
                                            ) : (
                                                <div
                                                    onClick={() => {
                                                        sendLink(
                                                            director.directorName,
                                                            setDisableSendLink,
                                                            index,
                                                            creditBureauConfig?.name,
                                                            'NEW_DIRECTOR',
                                                            director.phoneNo,
                                                            director.pan,
                                                        );
                                                    }}
                                                    className={clsx(styles.BannerText, {
                                                        [styles.DisabledBannerText]:
                                                            disableSendLink || !validateInput(director?.[dataToDisplay], dataToDisplay),
                                                    })}
                                                >
                                                    {director.isLoading ? (
                                                        <LoadingSpinner color={'var(--primary-text-colour)'} />
                                                    ) : (
                                                        <div className={styles.VerifyImg}>
                                                            {renderAsset(
                                                                disableSendLink ? VERIFY_GREY_CIRCLE : getVerifyIconLink(director),
                                                            )}
                                                            <span>Fetch</span>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        <CrossFilledIcon
                                            colour="var(--primary-text-colour)"
                                            className={styles.NewDirRemove}
                                            onClick={() => removeDirector(index)}
                                        />
                                    </div>
                                </div>
                            </div>
                        );
                    }
                })}
                <div className={styles.AddDirText}>
                    Add more directors for credit bureau check.{' '}
                    <span onClick={() => addDirector()} className={styles.AddDirCta}>
                        Add Director
                    </span>
                </div>
            </div>
        </div>
    );
}

export default DirectorDetails;
